/**
 * The soft masses behind the hero and the footer: three radial masses centred
 * below the canvas's bottom edge so only their upper shoulders are in frame,
 * drifting, breathing and stretching on unrelated clocks, plus a floor wash
 * along the bottom edge. One colour family at a time; the family walks on
 * every PER_FAMILY-th phrase change.
 *
 * Port of the prototype's footer-glow.js and hero-glow.js. The hero is the
 * footer's gradient on a light field; the ways it differs are the options in
 * PageGlowOptions, and everything else here is common to both.
 */

type Rgb = [number, number, number];

interface Family {
    core: string;
    light: string;
}

/**
 * The palette both glows read. Each family is a core and a light: the core
 * carries the body of a mass and the light its centre, so the pair is a tone
 * and its highlight rather than two colours. The gradient stops are fractions
 * of the alpha, so each alpha scales its whole glow.
 */
export const PAGEGLOW = {
    // Peak alpha of a mass in the hero. It can run far higher than the
    // footer's: multiplying into white has no ceiling, where 'lighter' over
    // #222 stacks one family's three masses until the sum clips to white.
    heroAlpha: 0.7,
    // And in the footer. Kept low on purpose: the brief is that the movement
    // should be more noticeable than the amount of colour.
    footerAlpha: 0.3,
    families: [
        { core: "#ea521a", light: "#ff4000" }, // orange, the hero's and Expertise's
        { core: "#0587c1", light: "#6cccf9" }, // blue, Work's base
        { core: "#289581", light: "#60f0df" }, // green, Proof's
    ] as const satisfies readonly Family[],
};

export interface PageGlowOptions {
    /**
     * 'lighter' clears to transparent and adds the masses, so they read as
     * light swelling off an edge on a dark field (footer). 'multiply' paints
     * the canvas white and multiplies the masses in; with the element
     * multiplied onto a light page, white is a no-op and every mass a tint
     * (hero). Overlaps darken where the footer's brighten.
     */
    composite: "lighter" | "multiply";
    /** Peak alpha of a mass. */
    alpha: number;
    /**
     * Shuffle which mass turns first on each hand-over, with jittered lags,
     * instead of the fixed per-mass lead. A constant lead in left/middle/right
     * order sweeps every change the same way across the frame, which reads as
     * a mechanism (hero).
     */
    shuffleHandover: boolean;
    /**
     * Let the pointer shift the masses' tone (hue along the walk and lift
     * toward the light shade) on a faster lag than their position (hero).
     */
    pointerTone: boolean;
    /**
     * CSS px the canvas overhangs its reference box on every side, beyond the
     * 80 the drawing was tuned with. Every position and radius is a fraction
     * of the reference box, so more bleed (for the blur to sample) is padding
     * rather than a shift of the whole field. 0 means the canvas is the
     * reference box.
     */
    extraBleed: number;
    /**
     * Below 1024 wide, lift the masses so the field still reaches the middle
     * of the reference box. Their radii are fractions of its width, so on a
     * narrow box the field otherwise falls away from the centre (hero).
     */
    liftWhenStacked: boolean;
}

export const HERO_GLOW: PageGlowOptions = {
    composite: "multiply",
    alpha: PAGEGLOW.heroAlpha,
    shuffleHandover: true,
    pointerTone: true,
    extraBleed: 100,
    liftWhenStacked: true,
};

export const FOOTER_GLOW: PageGlowOptions = {
    composite: "lighter",
    alpha: PAGEGLOW.footerAlpha,
    shuffleHandover: false,
    pointerTone: false,
    extraBleed: 0,
    liftWhenStacked: false,
};

// Render resolution as a fraction of layout size. A 1440-wide frame is ~230px
// across, so a frame costs almost nothing and the upscale has removed every
// hard edge before the CSS blur is asked to do anything.
export const SCALE = 0.16;

const PER_FAMILY = 3; // phrase changes per colour family
const XFADE = 2.6; // seconds a hand-over takes
const EASE = 0.028; // pointer lag per 60 Hz frame, deliberately very slow

// The pointer's second, faster lag, for colour only. At EASE a colour change
// is a drift nobody connects to the cursor; tone gets its own lag.
const EASE_C = 0.1;

// The prototype steps the lags once per frame, so they run twice as fast at
// 120 Hz. This scales a 60 Hz step to the frame's length instead: the same
// step at 60 Hz, the same time to arrive at any refresh rate.
const lag = (ease: number, dt: number) => 1 - Math.pow(1 - ease, dt * 60);
const TINT = 0.34; // family steps a mass is pushed along the walk, under the cursor
const LIFT = 0.38; // how far toward its own light shade it is lifted
const REACH_C = 1.9; // 1 / the half-width of the reaction, in canvas fractions

// Shuffled hand-over: the first mass turns at once, the others trail. All
// lags, never leads: a lead puts a mass part way into the new colour on the
// first frame, which pops. Total spread held near the fixed leads' 1.4s.
const LAG_MIN = 0.18; // seconds: the closest two masses may turn
const LAG_STEP = 0.5; // the most added on top, per mass

// Solved from the field itself: the lift that lands the top of the field on
// the centre agreed to three places across 375-1023 wide.
const Y_LIFT_K = 0.355;
const STACKED_MAX_WIDTH = 1023;

// cOff was a lead in steps of the 33.3s colour clock this replaced; it is kept
// as the same number of seconds.
const OLD_STEP = 1 / 0.03;

interface Mass {
    x: number; // fraction of the box; y >= 1 is at or below the bottom edge
    y: number;
    r: number; // fraction of the box's width
    pull: number; // fraction of the way toward the pointer
    cOff: number; // fixed colour lead, in OLD_STEPs
    dSpd: number; // drift clock
    dPh: number;
    ax: number; // drift range
    ay: number;
    rb: number; // how far the radius swells and shrinks
    rSpd: number; // breath clock
    rPh: number;
    el: number; // how far it stretches out of round
    elSpd: number; // how fast the stretch breathes
    aSpd: number; // how fast its long axis turns, signed
    aPh: number;
}

/*
 * The outer masses are pinned to the bottom corners so the glow reaches both
 * bottom edges, and barely follow the cursor (a stronger pull dragged the far
 * one inward and its corner went dark). The middle mass sits further under and
 * smaller: it joins the corners rather than being a third source, leaving the
 * darker arc between them, and does the responding to the pointer.
 *
 * No clock divides into another, so the three never fall into step and the
 * movement has no period. Stretching is area-preserving, so a mass keeps its
 * total light; the radii are ~10% larger than for a disc to keep the fill.
 */
const MASSES: readonly Mass[] = [
    {
        x: 0.02,
        y: 1.0,
        r: 0.575,
        pull: 0.12,
        cOff: -0.02,
        dSpd: 1.62,
        dPh: 0.0,
        ax: 0.23,
        ay: 0.15,
        rb: 0.34,
        rSpd: 0.76,
        rPh: 0.0,
        el: 0.62,
        elSpd: 0.43,
        aSpd: 0.185,
        aPh: 0.0,
    },
    {
        x: 0.5,
        y: 1.26,
        r: 0.49,
        pull: 0.3,
        cOff: 0.0,
        dSpd: 1.21,
        dPh: 2.3,
        ax: 0.36,
        ay: 0.21,
        rb: 0.42,
        rSpd: 0.56,
        rPh: 2.9,
        el: 0.78,
        elSpd: 0.31,
        aSpd: -0.145,
        aPh: 2.1,
    },
    {
        x: 0.98,
        y: 0.99,
        r: 0.555,
        pull: 0.1,
        cOff: 0.022,
        dSpd: 1.39,
        dPh: 4.1,
        ax: 0.23,
        ay: 0.15,
        rb: 0.3,
        rSpd: 0.67,
        rPh: 5.2,
        el: 0.58,
        elSpd: 0.37,
        aSpd: 0.215,
        aPh: 4.4,
    },
];

const hexToRgb255 = (hex: string): Rgb => {
    const v = parseInt(hex.replace("#", ""), 16);
    return [(v >> 16) & 255, (v >> 8) & 255, v & 255];
};

// [family][0 = core, 1 = light]
const FAMILIES: readonly (readonly [Rgb, Rgb])[] = PAGEGLOW.families.map(
    (f) => [hexToRgb255(f.core), hexToRgb255(f.light)] as const,
);

const smooth = (t: number) => t * t * (3 - 2 * t);

const rgba = (c: Rgb, a: number) => `rgba(${c.join(",")},${a.toFixed(3)})`;

export class PageGlowEngine {
    private readonly ctx: CanvasRenderingContext2D | null;

    // The bitmap, and the reference box the drawing is solved in, placed
    // inside it at (ox, oy). All in bitmap px.
    private w = 0;
    private h = 0;
    private rw = 0;
    private rh = 0;
    private ox = 0;
    private oy = 0;

    // Pointer, in fractions of the canvas box. Starting at the centre-bottom,
    // where the masses live, makes its first appearance a nudge, not a jump.
    private tx = 0.5;
    private ty = 1.0;
    private cx = 0.5;
    private cy = 1.0;
    private ccx = 0.5;
    private ccy = 1.0;
    // Pointer engagement: 0 until the pointer moves, so an untouched page
    // shows the designed resting state.
    private amp = 0;
    private ampT = 0;

    private fam = 0; // the family the current hand-over starts from
    private famT0 = Infinity; // when it started, in seconds; Infinity = resting
    private swaps = 0;
    private readonly leads: number[] = MASSES.map((m) => m.cOff * OLD_STEP);

    constructor(
        private readonly canvas: HTMLCanvasElement,
        private readonly options: PageGlowOptions,
    ) {
        this.ctx = canvas.getContext("2d");
    }

    /** Re-read the canvas's CSS box. */
    resize() {
        const rect = this.canvas.getBoundingClientRect();
        this.w = Math.max(1, Math.round(rect.width * SCALE));
        this.h = Math.max(1, Math.round(rect.height * SCALE));
        if (this.canvas.width !== this.w) this.canvas.width = this.w;
        if (this.canvas.height !== this.h) this.canvas.height = this.h;
        const inset = this.options.extraBleed * 2;
        this.rw = Math.max(1, (rect.width - inset) * SCALE);
        this.rh = Math.max(1, (rect.height - inset) * SCALE);
        this.ox = (this.w - this.rw) / 2;
        this.oy = (this.h - this.rh) / 2;
    }

    /** Feed a pointer position, in client px. */
    pointer(clientX: number, clientY: number) {
        const rect = this.canvas.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        this.tx = (clientX - rect.left) / rect.width;
        this.ty = (clientY - rect.top) / rect.height;
        this.ampT = 1;
    }

    /**
     * The headline changed phrase. Every PER_FAMILY-th one hands over to the
     * next family. `fam` is re-read from the walk's position so a change
     * arriving mid-hand-over resolves to the nearest family.
     */
    phrase(now: number) {
        if (++this.swaps % PER_FAMILY) return;
        const t = now / 1000;
        this.fam = Math.round(this.phaseAt(t, 0));
        this.famT0 = t;
        // Before the fade starts: re-rolling mid-fade would teleport a mass.
        if (this.options.shuffleHandover) this.shuffleLeads();
    }

    /**
     * Draw the frame at `now` (ms, performance.now clock), `dt` seconds after
     * the previous one. A redraw that isn't a new frame passes 0.
     */
    draw(now: number, dt: number) {
        const ctx = this.ctx;
        if (!ctx) return;
        const { w, h, rw, rh, ox, oy } = this;
        const alpha = this.options.alpha;
        const t = now / 1000;

        const k = lag(EASE, dt);
        const kC = lag(EASE_C, dt);
        this.cx += (this.tx - this.cx) * k;
        this.cy += (this.ty - this.cy) * k;
        this.ccx += (this.tx - this.ccx) * kC;
        this.ccy += (this.ty - this.ccy) * kC;
        this.amp += (this.ampT - this.amp) * kC;

        // The pointer is tracked over the whole window, so clamp to the box.
        const py = Math.min(1, Math.max(0, this.ccy));
        // How strongly the pointer reaches a mass at fraction bx: x only,
        // since every mass sits on or under the bottom edge; y is a gentle
        // overall gain.
        const reach = (bx: number) =>
            this.options.pointerTone
                ? this.amp *
                  Math.max(0, 1 - Math.abs(this.ccx - bx) * REACH_C) *
                  (0.55 + 0.45 * py)
                : 0;

        const yLift =
            this.options.liftWhenStacked &&
            window.innerWidth <= STACKED_MAX_WIDTH
                ? Math.max(0, 0.5 - Y_LIFT_K * (rw / rh))
                : 0;

        if (this.options.composite === "multiply") {
            ctx.globalCompositeOperation = "source-over";
            ctx.fillStyle = "#fff";
            ctx.fillRect(0, 0, w, h);
            ctx.globalCompositeOperation = "multiply";
        } else {
            ctx.clearRect(0, 0, w, h);
            ctx.globalCompositeOperation = "lighter";
        }

        MASSES.forEach((b, i) => {
            // Three sines per axis at unrelated rates: with two the path
            // closes on itself often enough to read as a wobble. x and y share
            // no frequency, so the mass traces loops, not a line.
            const p = t * b.dSpd + b.dPh;
            const dx =
                b.ax *
                (0.46 * Math.sin(p) +
                    0.31 * Math.sin(p * 0.43 + 1.7) +
                    0.23 * Math.sin(p * 0.19 + 3.1));
            const dy =
                b.ay *
                (0.44 * Math.sin(p * 0.71 + 0.9) +
                    0.33 * Math.sin(p * 0.31) +
                    0.23 * Math.sin(p * 0.13 + 2.2));

            // A different fraction of the way toward the lagged pointer per
            // mass, so the group opens out toward it instead of sliding with
            // it. Vertical pull is halved again: the masses belong to the edge.
            const x = (b.x + dx + (this.cx - (b.x + dx)) * b.pull) * rw + ox;
            const y =
                (b.y + dy + (this.cy - (b.y + dy)) * b.pull * 0.45 - yLift) *
                    rh +
                oy;

            // The radius breathes, slower than the drift; the pointer adds a
            // little to the same number rather than switching state.
            const q = t * b.rSpd + b.rPh;
            const breath =
                1 +
                b.rb * (0.62 * Math.sin(q) + 0.38 * Math.sin(q * 0.37 + 1.1));
            const near = Math.max(0, 1 - Math.abs(this.cx - b.x) * 2.2);
            const r = b.r * rw * breath * (1 + 0.12 * near);

            // Drawn as an ellipse whose long axis turns, so a mass is a tongue
            // at one moment and nearly round at another.
            const e =
                1 +
                b.el *
                    (0.6 * Math.sin(t * b.elSpd + b.rPh) +
                        0.4 * Math.sin(t * b.elSpd * 0.41 + 2.4));
            const ang =
                t * b.aSpd + b.aPh + 0.35 * Math.sin(t * b.aSpd * 0.6 + b.dPh);

            const nearC = reach(b.x);
            const core = this.colourAt(i, t, 0, nearC * TINT, nearC * LIFT);
            const light = this.colourAt(i, t, 1, nearC * TINT, 0);

            ctx.save();
            ctx.translate(x, y);
            ctx.rotate(ang);
            ctx.scale(e, 1 / e); // area-preserving, so stretching doesn't brighten
            const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
            g.addColorStop(0, rgba(light, alpha));
            g.addColorStop(0.2, rgba(core, alpha * 0.86));
            g.addColorStop(0.5, rgba(core, alpha * 0.48));
            g.addColorStop(0.8, rgba(core, alpha * 0.14));
            g.addColorStop(1, rgba(core, 0));
            ctx.fillStyle = g;
            // The fill has to cover the canvas in the transformed space.
            const span = (w + h) * Math.max(e, 1 / e);
            ctx.fillRect(-span, -span, span * 2, span * 2);
            ctx.restore();
        });

        // The floor. The bottom corners sit past the masses' falloff however
        // large they are, and widening them would push light into the middle,
        // which has to stay clear. A flat wash along the bottom edge fills the
        // corners by construction. Softer lift under the pointer: across the
        // whole edge the full lift reads as the page brightening.
        const fNear = reach(MASSES[1].x);
        const floor = this.colourAt(1, t, 0, fNear * TINT, fNear * LIFT * 0.6);
        const fl = ctx.createLinearGradient(0, oy + rh, 0, oy + rh * 0.72);
        fl.addColorStop(0, rgba(floor, alpha * 0.52));
        fl.addColorStop(0.42, rgba(floor, alpha * 0.2));
        fl.addColorStop(1, rgba(floor, 0));
        ctx.fillStyle = fl;
        ctx.fillRect(0, 0, w, h);

        ctx.globalCompositeOperation = "source-over";
    }

    // Where the walk has got to at time t for a mass `lead` seconds ahead (+)
    // or behind (-). Rests on an integer between hand-overs.
    private phaseAt(t: number, lead: number) {
        const e = t - this.famT0 + lead;
        return this.fam + (e <= 0 ? 0 : e >= XFADE ? 1 : smooth(e / XFADE));
    }

    // Mass i's colour on the orange -> blue -> green walk. `tint` pushes it a
    // fraction of a step further along the walk, so the hue never leaves the
    // palette; `lift` blends toward the same position in the light shade, so
    // the field brightens without changing hue.
    private colourAt(
        i: number,
        t: number,
        shade: 0 | 1,
        tint: number,
        lift: number,
    ): Rgb {
        const f = (((this.phaseAt(t, this.leads[i]) + tint) % 3) + 3) % 3;
        const k = Math.floor(f);
        const m = f - k;
        const mix = (sh: 0 | 1): Rgb => {
            const a = FAMILIES[k][sh];
            const b = FAMILIES[(k + 1) % 3][sh];
            return [
                a[0] + (b[0] - a[0]) * m,
                a[1] + (b[1] - a[1]) * m,
                a[2] + (b[2] - a[2]) * m,
            ];
        };
        const c = mix(shade);
        if (lift) {
            const l = mix(1);
            for (let n = 0; n < 3; n++) c[n] += (l[n] - c[n]) * lift;
        }
        return [Math.round(c[0]), Math.round(c[1]), Math.round(c[2])];
    }

    private shuffleLeads() {
        const order = [0, 1, 2];
        for (let i = order.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [order[i], order[j]] = [order[j], order[i]];
        }
        let lag = 0;
        order.forEach((mi, n) => {
            if (n) lag += LAG_MIN + Math.random() * LAG_STEP;
            this.leads[mi] = -lag;
        });
    }
}
